import { Injectable, inject, signal, WritableSignal } from '@angular/core';
import { Observable, forkJoin, of, timer } from 'rxjs';
import { map, first, catchError, switchMap, takeWhile } from 'rxjs/operators';
import {ApiCallService} from "./api-call.service";
import {RemoteResponse} from "../shared/remote-response";
import {Session} from "./session.service";
import { AccountStatus } from '../interfaces/account-status';
import {StripePaymentElementOptions} from "@stripe/stripe-js";

export interface SubscriptionPlan {
  requestKey: string;
  name: string;
  price: number;
  isTrial: boolean;
  pricePerSeat: number;
}

export interface OrderStatusResult {
  success: boolean;
  orderSuccess?: boolean;
  status: 'pending' | 'complete' | 'error';
  stripeVerificationUrl?: string;
  orderAmount?: number;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private api = inject(ApiCallService);
  private session = inject(Session);
  private readonly PENDING_PAYMENT_KEY = 'pendingPayment';

  private accountStatusSignal: WritableSignal<AccountStatus | null> = signal<AccountStatus | null>(null);
  public get accountStatus() {
    return this.accountStatusSignal;
  }
  public setAccountStatus(status: AccountStatus) {
    this.accountStatusSignal.set(status);
  }

  public getPaymentElementOptions(layout: 'accordion' | 'tabs' = 'tabs'): StripePaymentElementOptions {
    return {
      defaultValues: {
        billingDetails: {
          name: this.session.getUser().userFullName,
          email: this.session.getUser().userData.email,
        },
      },
      layout: {
        type: layout,
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: false
      }
    };
  }

  public initAccountStatus() {
    this.setAccountStatus(
      this.session.getAccountStatus()
    )
  }

  pollOrderStatus(clientSecret: string): Observable<OrderStatusResult> {
    const maxRetries = 45;
    return timer(0, 1000).pipe(
      takeWhile((_, index) => index < maxRetries, true),
      switchMap((_, index) => {
        if (index >= maxRetries) {
          return of({ success: false, status: 'error', error: 'Payment verification timed out. Please check your account status or contact support.' } as OrderStatusResult);
        }
        return this.api.postJsonData("/getOrderStatus", { clientSecret });
      }),
      map((resp: OrderStatusResult | RemoteResponse) => {
        if (resp && 'status' in resp && resp.status === 'error') {
          return resp as OrderStatusResult;
        }
        if (resp && resp.success && 'data' in resp) {
          const orderData = resp.data[0].dataSet;
          const orderComplete = orderData.orderComplete;
          const orderSuccess = orderData.orderSuccess;
          const stripeVerificationUrl = orderData.stripeVerificationUrl;
          const orderAmount = orderData.orderAmount;

          if (orderComplete) {
            if (orderSuccess) {
              return { success: true, orderSuccess: true, status: 'complete', orderAmount } as OrderStatusResult;
            } else {
              return { success: true, orderSuccess: false, status: 'error', error: 'Payment failed.' } as OrderStatusResult;
            }
          } else if (stripeVerificationUrl) {
            return { success: true, status: 'pending', stripeVerificationUrl } as OrderStatusResult;
          } else {
            return { success: true, status: 'pending' } as OrderStatusResult;
          }
        } else {
          const errorMsg = resp && 'dataError' in resp && Array.isArray(resp.dataError) && resp.dataError.length > 0
            ? resp.dataError[0].message || resp.dataError[0].description
            : 'Payment verification failed.';
          return { success: false, status: 'error', error: errorMsg } as OrderStatusResult;
        }
      }),
      takeWhile(res => res.status === 'pending' && !res.stripeVerificationUrl, true),
      catchError(err => of({ success: false, status: 'error', error: err?.message || 'Unknown error occurred.' } as OrderStatusResult))
    );
  }

  getPlanDetailsAndPricing(): Observable<{ plan: SubscriptionPlan | null, seatCount: number, members: any[], error?: string }> {
    return forkJoin({
      plansResp: this.api.postJsonData('/public/getSubscriptionList', {}),
      seatsResp: this.api.postJsonData('/getAccountStatus', {}),
    }).pipe(
      first(),
      map(({ plansResp, seatsResp }) => {
        let plan = null;
        let seatCount = 0;
        let members: any[] = [];
        let error: string | undefined = undefined;
        if (!plansResp || plansResp.success === false) {
          error = Array.isArray(plansResp?.dataError) && plansResp.dataError.length > 0
            ? plansResp.dataError[0].message || plansResp.dataError[0].description
            : 'Failed to load subscription list.';
        } else {
          const plans = plansResp.data[0].dataSet.map((plan: any) => ({
            requestKey: plan.requestKey,
            name: plan.name,
            price: plan.amount,
            isTrial: plan.isTrial,
            pricePerSeat: plan.accountAdditionalUserCharge || 0,
          }));
          plan = plans.find((p: any) => p.isTrial === false) ?? null;
        }
        if (!seatsResp || seatsResp.success === false) {
          error = Array.isArray(seatsResp?.dataError) && seatsResp.dataError.length > 0
            ? seatsResp.dataError[0].message || seatsResp.dataError[0].description
            : 'Failed to load account status.';
        } else {
          let accountUsers = Array.isArray(seatsResp.data[0].dataSet.accountUsers) ? seatsResp.data[0].dataSet.accountUsers.filter(u => u.email !== this.session.getUser()?.userData.email) : [];
          seatCount = accountUsers.length;
          members = accountUsers;
        }
        return { plan, seatCount, members, error };
      }),
      catchError((err) => of({ plan: null, seatCount: 0, members: [], error: err?.message || 'Unknown error occurred.' }))
    );
  }

  createPaymentIntent(planRequestKey: string, emailsProjects?: { emails: string[], projects: string[]}): Observable<{ paymentIntentClientSecret: string | null, customerSessionClientSecret: string | null, error?: string }> {
    if (!planRequestKey) return of({ paymentIntentClientSecret: null, customerSessionClientSecret: null, error: 'No plan request key provided.' });
    const postObj = { subscriptionRequestKey: planRequestKey };
    if (emailsProjects) {
      const emails = emailsProjects.emails;
      const projects = emailsProjects.projects;
      postObj['newMembers'] = emails.map(email => ({ email, projects }));
    }
    return this.api.postJsonData('/getPaymentIntent', postObj)
      .pipe(
        first(),
        map(response => {
          if (response && response.success) {
            return {
              paymentIntentClientSecret: response.data[0].dataSet.paymentIntentClientSecret,
              customerSessionClientSecret: response.data[0].dataSet.customerSessionClientSecret,
            };
          } else {
            const errorMsg = Array.isArray(response?.dataError) && response.dataError.length > 0
              ? response.dataError[0].message || response.dataError[0].description
              : 'Failed to create payment intent.';
            return { paymentIntentClientSecret: null, customerSessionClientSecret: null, error: errorMsg };
          }
        }),
        catchError((err) => of({ paymentIntentClientSecret: null, customerSessionClientSecret: null, error: err?.message || 'Unknown error occurred.' }))
      );
  }

  // Create a function called setPendingPayment that will set to the localstorage the paymentIntentClientSecret. This function will be called before the users get redirected to Stripe for authentication.
  setPendingPayment(paymentIntentClientSecret: string): void {
    const pendingData = {
      paymentIntentClientSecret,
      timestamp: Date.now(),
      status: 'pending',
    }
    localStorage.setItem(this.PENDING_PAYMENT_KEY, JSON.stringify(pendingData));
  }

  // Create a function called getPendingPayment that will get from the localstorage the paymentIntentClientSecret. This function will be called when the user returns from Stripe after authentication.
  getPendingPayment(): any | null {
    const pendingData = localStorage.getItem(this.PENDING_PAYMENT_KEY);
    return pendingData ? JSON.parse(pendingData) : null;
  }

  // check if the paymentIntentClientSecret is still valid
  isPendingPaymentValid(paymentIntentClientSecret: string): boolean {
    const pendingData = this.getPendingPayment();
    const thirtyMinutesAgo = Date.now() - 1000 * 60 * 30;
    return pendingData && pendingData.paymentIntentClientSecret === paymentIntentClientSecret && pendingData.timestamp >= thirtyMinutesAgo;
  }

  // Create a function called clearPendingPayment that will remove from the localstorage the paymentIntentClientSecret. This function will be called after the payment is completed or failed.
  clearPendingPayment(): void {
    localStorage.removeItem(this.PENDING_PAYMENT_KEY);
  }

}
